/**
 * Module Service Scolarité — pilotage des tâches et rapports.
 * - Responsable : dashboard KPIs, création tâches multi-agents / multi-pôles,
 *   rapports agents classés par jour/semaine/pôle, rappels push.
 * - Agent : ses tâches, son rapport du jour, historique perso.
 *
 * Charte sobre : pas de gradients, pas d'emoji, peu d'icônes, hiérarchie
 * neutre. Dark mode natif.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Circle, Clock, AlertTriangle, Plus, Filter, X, MessageSquare,
  Users, User, Send, Save, ChevronDown, ChevronRight, Trash2, RefreshCw,
  FileText, LayoutDashboard, Eye, Bell, Search, Layers, BellRing, Calendar,
  Lock, Unlock, ClipboardList,
} from 'lucide-react';
import type { RootState } from '../../store';
import FilCommentairesRapport from '../../components/ServiceScolarite/FilCommentairesRapport';
import {
  useListTachesQuery, useCreateTacheMutation,
  useDeleteTacheMutation, useCompleterTacheMutation, useRouvrirTacheMutation,
  useCommenterTacheMutation, useRappelerTacheMutation,
  useStatsTachesQuery,
  useListRapportsQuery, useGetRapportAujourdhuiQuery, useSaveRapportMutation,
  useMarquerRapportLuMutation, useListAgentsQuery, useListPolesQuery,
  useAgentsSansRapportQuery, useRappelerAgentsRapportMutation,
  useStatsRapportsQuery,
  useListReunionsQuery, useCreerReunionMutation,
  useModifierReunionMutation, useSupprimerReunionMutation,
  useListeComptesVerrouillesQuery, useLeverVerrouRapportMutation,
  TacheScolarite, PrioriteTache, StatutTache, TypeTache, RapportReunion,
} from '../../services/serviceScolariteApi';

// ─── Styles helpers ─────────────────────────────────────────────────────
const STATUT_LABELS: Record<StatutTache, string> = {
  a_faire: 'À faire',
  en_cours: 'En cours',
  terminee: 'Terminée',
  annulee: 'Annulée',
};
const STATUT_STYLE: Record<StatutTache, string> = {
  a_faire: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900',
  en_cours: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  terminee: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  annulee: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800/60 dark:text-gray-400 dark:border-gray-700',
};
const PRIORITE_STYLE: Record<PrioriteTache, string> = {
  normale: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  haute: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  urgente: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
};

// ─── Utilitaires ────────────────────────────────────────────────────────
function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function formatDateShort(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}
function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}
function initiales(nom: string): string {
  return (nom || '?').split(' ').slice(0, 2).map(s => s[0] || '').join('').toUpperCase();
}
function isoAujourdhui(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── Composant principal ────────────────────────────────────────────────
type TabKey = 'dashboard' | 'taches' | 'mon-rapport' | 'rapports' | 'stats' | 'reunions';

export default function ServiceScolarite() {
  const user = useSelector((s: RootState) => s.auth.user) as any;
  const isDark = useSelector((s: RootState) => s.ui.theme) === 'dark';
  const isResponsable = user?.role === 'super_admin' || user?.role === 'admin';

  // Lire l'onglet dans le hash (permet aux notifications push d'ouvrir un onglet précis)
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    const url = new URL(window.location.href);
    const t = url.searchParams.get('tab') as TabKey | null;
    if (t && ['dashboard', 'taches', 'mon-rapport', 'rapports', 'stats', 'reunions'].includes(t)) return t;
    return isResponsable ? 'dashboard' : 'taches';
  });

  const bg = isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900';

  return (
    <div className={`p-4 sm:p-6 min-h-screen ${bg}`}>
      <header className="mb-5">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-300 flex-shrink-0">
            <LayoutDashboard size={20} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold leading-tight">Service Scolarité</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {isResponsable
                ? 'Pilotage des tâches et rapports du service à distance.'
                : 'Vos tâches à faire et rapport journalier.'}
            </p>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className={`flex gap-1 mb-5 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'} overflow-x-auto`}>
        {isResponsable && (
          <TabBtn active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} icon={<LayoutDashboard size={15} />}>
            Tableau de bord
          </TabBtn>
        )}
        <TabBtn active={activeTab === 'taches'} onClick={() => setActiveTab('taches')} icon={<CheckCircle2 size={15} />}>
          {isResponsable ? 'Toutes les tâches' : 'Mes tâches'}
        </TabBtn>
        <TabBtn active={activeTab === 'mon-rapport'} onClick={() => setActiveTab('mon-rapport')} icon={<FileText size={15} />}>
          Mon rapport
        </TabBtn>
        {/* Rapports de réunion : visible à TOUS pour traçabilité */}
        <TabBtn active={activeTab === 'reunions'} onClick={() => setActiveTab('reunions')} icon={<Users size={15} />}>
          Réunions
        </TabBtn>
        {isResponsable && (
          <>
            <TabBtn active={activeTab === 'rapports'} onClick={() => setActiveTab('rapports')} icon={<FileText size={15} />}>
              Rapports agents
            </TabBtn>
            <TabBtn active={activeTab === 'stats'} onClick={() => setActiveTab('stats')} icon={<Layers size={15} />}>
              Statistiques
            </TabBtn>
          </>
        )}
      </nav>

      {activeTab === 'dashboard' && <DashboardTab isDark={isDark} />}
      {activeTab === 'taches' && <TachesTab isDark={isDark} isResponsable={isResponsable} userId={user?.id} />}
      {activeTab === 'mon-rapport' && <MonRapportTab isDark={isDark} />}
      {activeTab === 'reunions' && <ReunionsTab isDark={isDark} isResponsable={isResponsable} userId={user?.id} />}
      {activeTab === 'rapports' && <RapportsAgentsTab isDark={isDark} />}
      {activeTab === 'stats' && <StatsRapportsTab isDark={isDark} />}
    </div>
  );
}

const TabBtn: React.FC<{
  active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode;
}> = ({ active, onClick, icon, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${
      active
        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
    }`}
  >
    {icon}
    {children}
  </button>
);

// ═══════════════════════════════════════════════════════════════════════
// Dashboard responsable — KPIs sobres
// ═══════════════════════════════════════════════════════════════════════
const DashboardTab: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const { data: stats } = useStatsTachesQuery();
  const { data: rapports = [] } = useListRapportsQuery({ non_lus: true });
  const { data: sansRapport } = useAgentsSansRapportQuery();
  const [rappelerAgents, { isLoading: sendingRappel }] = useRappelerAgentsRapportMutation();
  const [feedback, setFeedback] = useState<string | null>(null);
  // Comptes verrouillés (accessible admin + super_admin uniquement — le
  // backend rejette avec 403 pour les autres, on skippe donc silencieusement)
  const { data: verrouStatus, refetch: refetchVerrous } = useListeComptesVerrouillesQuery(
    undefined, { pollingInterval: 60_000 },
  );
  const [leverVerrou, { isLoading: unlocking }] = useLeverVerrouRapportMutation();
  const [confirmUnlockId, setConfirmUnlockId] = useState<number | null>(null);

  const handleUnlock = async (userId: number, nom: string) => {
    if (!window.confirm(
      `Débloquer le compte de ${nom} ?\n\n` +
      "L'agent regagnera l'accès complet immédiatement, même s'il n'a pas rédigé " +
      "son rapport ni complété ses tâches en retard.\n\nConfirmer ?"
    )) return;
    setConfirmUnlockId(userId);
    try {
      const r = await leverVerrou(userId).unwrap();
      refetchVerrous();
      setFeedback(`Compte de ${r.nom} débloqué.`);
      setTimeout(() => setFeedback(null), 4000);
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    } finally {
      setConfirmUnlockId(null);
    }
  };

  const card = "rounded-xl border p-4 " + (isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200");

  const kpiCard = (label: string, value: number | string, tone: 'neutral' | 'blue' | 'amber' | 'green' | 'red' | 'orange', icon: React.ReactNode) => {
    const tones: Record<string, string> = {
      neutral: 'text-gray-800 dark:text-gray-100',
      blue: 'text-blue-600 dark:text-blue-400',
      amber: 'text-amber-600 dark:text-amber-400',
      green: 'text-emerald-600 dark:text-emerald-400',
      red: 'text-red-600 dark:text-red-400',
      orange: 'text-orange-600 dark:text-orange-400',
    };
    return (
      <div className={card}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">{label}</div>
            <div className={`text-2xl font-bold mt-1 tabular-nums ${tones[tone]}`}>{value}</div>
          </div>
          <div className={`${tones[tone]} opacity-70 mt-0.5`}>{icon}</div>
        </div>
      </div>
    );
  };

  const rappelerTousManquants = async () => {
    if (!sansRapport || sansRapport.nb_manquants === 0) return;
    if (!window.confirm(`Envoyer un rappel push à ${sansRapport.nb_manquants} agent(s) sans rapport aujourd'hui ?`)) return;
    setFeedback(null);
    try {
      const r = await rappelerAgents().unwrap();
      setFeedback(`${r.nb_pushs_envoyees} push(s) envoyée(s) à ${r.nb_destinataires} agent(s).`);
    } catch (e: any) {
      setFeedback(e?.data?.detail || 'Erreur d\'envoi');
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiCard('Total', stats?.total ?? 0, 'neutral', <CheckCircle2 size={18} />)}
        {kpiCard('À faire', stats?.a_faire ?? 0, 'blue', <Circle size={18} />)}
        {kpiCard('En cours', stats?.en_cours ?? 0, 'amber', <Clock size={18} />)}
        {kpiCard('Terminées', stats?.terminees ?? 0, 'green', <CheckCircle2 size={18} />)}
        {kpiCard('En retard', stats?.en_retard ?? 0, 'red', <AlertTriangle size={18} />)}
        {kpiCard('Urgentes', stats?.urgentes ?? 0, 'orange', <AlertTriangle size={18} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Rapports non lus */}
        <div className={card}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Rapports non lus</h3>
            <span className="text-xs text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 rounded-full px-2 py-0.5 tabular-nums">
              {rapports.length}
            </span>
          </div>
          {rapports.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun rapport en attente de lecture.</p>
          ) : (
            <ul className="space-y-2">
              {rapports.slice(0, 5).map(r => (
                <li key={r.id} className={`p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-50'} text-sm`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-[10px] font-bold text-blue-700 dark:text-blue-300 flex-shrink-0">
                      {initiales(r.auteur_nom)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.auteur_nom}</div>
                      <div className="text-[11px] text-gray-500">{formatDate(r.date_rapport)}</div>
                    </div>
                  </div>
                  {r.resume_libre && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 pl-9">{r.resume_libre}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Agents sans rapport */}
        <div className={card}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Agents sans rapport aujourd'hui</h3>
            <span className={`text-xs rounded-full px-2 py-0.5 tabular-nums ${
              (sansRapport?.nb_manquants ?? 0) > 0
                ? 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40'
                : 'text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40'
            }`}>
              {sansRapport?.nb_manquants ?? 0}
            </span>
          </div>
          {!sansRapport || sansRapport.nb_manquants === 0 ? (
            <p className="text-sm text-gray-500">Tous les agents ont saisi leur rapport aujourd'hui.</p>
          ) : (
            <>
              <ul className="space-y-1.5 max-h-60 overflow-y-auto">
                {sansRapport.agents.map(a => (
                  <li key={a.user_id} className={`flex items-center gap-2 p-2 rounded ${isDark ? 'bg-gray-800' : 'bg-gray-50'} text-sm`}>
                    <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-300 flex-shrink-0">
                      {initiales(a.nom_complet)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{a.nom_complet}</div>
                      <div className="text-[10px] text-gray-500 truncate">
                        {a.role} {a.poles?.length > 0 ? ' · ' + a.poles.join(' · ') : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <button
                onClick={rappelerTousManquants}
                disabled={sendingRappel}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
              >
                <BellRing size={14} />
                {sendingRappel ? 'Envoi…' : `Rappeler les ${sansRapport.nb_manquants} agent(s)`}
              </button>
              {feedback && (
                <div className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400">{feedback}</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ═══ Comptes verrouillés — admin / super_admin ═══ */}
      {verrouStatus && (
        <div className={card}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm inline-flex items-center gap-2">
              <Lock size={14} className={
                verrouStatus.nb_verrouilles > 0 ? 'text-red-500' : 'text-emerald-500'
              } />
              Comptes verrouillés
            </h3>
            <span className={`text-xs rounded-full px-2 py-0.5 tabular-nums ${
              verrouStatus.nb_verrouilles > 0
                ? 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40'
                : 'text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40'
            }`}>
              {verrouStatus.nb_verrouilles}
            </span>
          </div>
          {verrouStatus.nb_verrouilles === 0 ? (
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-500" />
              Aucun compte verrouillé actuellement.
              <span className="text-[11px] text-gray-400 ml-2">
                ({verrouStatus.nb_agents} agent(s) suivi(s))
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {verrouStatus.verrouilles.map(u => (
                <div key={u.user_id} className={`p-3 rounded-lg border ${
                  isDark ? 'bg-red-950/20 border-red-800' : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center text-red-700 dark:text-red-300 font-bold text-xs flex-shrink-0">
                      {(u.nom_complet || '?').split(' ').slice(0, 2).map(s => s[0] || '').join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-sm">{u.nom_complet}</div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase">{u.role}</span>
                        {u.nb_taches_retard > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-semibold">
                            {u.nb_taches_retard} tâche(s) en retard
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">
                        {u.motif || 'Rapport journalier manquant'}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        Verrou jusqu'au {new Date(u.jusqu_a).toLocaleString('fr-FR')}
                        {u.derniere_connexion && ` · dernière connexion ${new Date(u.derniere_connexion).toLocaleString('fr-FR')}`}
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnlock(u.user_id, u.nom_complet)}
                      disabled={unlocking && confirmUnlockId === u.user_id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50 flex-shrink-0"
                    >
                      <Unlock size={12} />
                      {unlocking && confirmUnlockId === u.user_id ? '…' : 'Débloquer'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vue d'ensemble tous les agents (dépliable) */}
          {verrouStatus.agents.length > 0 && (
            <details className="mt-4">
              <summary className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                Voir tous les agents ({verrouStatus.nb_agents})
              </summary>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      <th className="text-left py-1 pr-2">Agent</th>
                      <th className="text-left py-1 pr-2">Rapport aujourd'hui</th>
                      <th className="text-left py-1 pr-2">Rapport jour ouvré</th>
                      <th className="text-left py-1 pr-2">Tâches retard</th>
                      <th className="text-left py-1 pr-2">Statut</th>
                      <th className="text-right py-1">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                    {verrouStatus.agents.map(a => (
                      <tr key={a.user_id}>
                        <td className="py-1.5 pr-2 font-medium">{a.nom_complet} <span className="text-[10px] text-gray-500 uppercase">({a.role})</span></td>
                        <td className="py-1.5 pr-2">
                          {a.rapport_aujourdhui
                            ? <CheckCircle2 size={12} className="text-emerald-500 inline" />
                            : <X size={12} className="text-red-500 inline" />}
                        </td>
                        <td className="py-1.5 pr-2">
                          {a.rapport_jour_ouvre
                            ? <CheckCircle2 size={12} className="text-emerald-500 inline" />
                            : <X size={12} className="text-red-500 inline" />}
                        </td>
                        <td className="py-1.5 pr-2 tabular-nums">
                          {a.nb_taches_retard > 0
                            ? <span className="text-amber-600 font-semibold">{a.nb_taches_retard}</span>
                            : <span className="text-emerald-600">0</span>}
                        </td>
                        <td className="py-1.5 pr-2">
                          {a.verrouille
                            ? <span className="inline-flex items-center gap-0.5 text-red-600 font-semibold"><Lock size={10} /> Verrouillé</span>
                            : <span className="text-emerald-600">Actif</span>}
                        </td>
                        <td className="py-1.5 text-right">
                          {a.verrouille && (
                            <button
                              onClick={() => handleUnlock(a.user_id, a.nom_complet)}
                              disabled={unlocking && confirmUnlockId === a.user_id}
                              className="text-[10px] px-2 py-0.5 rounded border border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white disabled:opacity-50"
                            >
                              Débloquer
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-gray-500 mt-2">
                Référence : rapport du {new Date(verrouStatus.jour_ouvre_reference).toLocaleDateString('fr-FR')} (dernier jour ouvré) ou d'aujourd'hui.
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Onglet Tâches
// ═══════════════════════════════════════════════════════════════════════
const TachesTab: React.FC<{ isDark: boolean; isResponsable: boolean; userId: number }> = ({
  isDark, isResponsable, userId,
}) => {
  const [filtreStatut, setFiltreStatut] = useState<StatutTache | ''>('');
  const [filtrePole, setFiltrePole] = useState<string>('');
  const [showCreate, setShowCreate] = useState(false);
  const [tacheDetail, setTacheDetail] = useState<TacheScolarite | null>(null);

  const { data: taches = [], refetch } = useListTachesQuery(
    { ...(filtreStatut ? { statut: filtreStatut as StatutTache } : {}),
      ...(filtrePole ? { pole: filtrePole } : {}) },
  );
  const { data: poles = [] } = useListPolesQuery();
  const [completer] = useCompleterTacheMutation();
  const [rouvrir] = useRouvrirTacheMutation();
  const [deleteTache] = useDeleteTacheMutation();

  // Ouverture profonde via ?tache=ID
  useEffect(() => {
    const url = new URL(window.location.href);
    const tid = url.searchParams.get('tache');
    if (tid && taches.length) {
      const t = taches.find(t => t.id === Number(tid));
      if (t) setTacheDetail(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taches.length]);

  const tachesFiltrees = useMemo(() => {
    return [...taches].sort((a, b) => {
      const aRetard = a.est_en_retard ? 1 : 0;
      const bRetard = b.est_en_retard ? 1 : 0;
      if (aRetard !== bRetard) return bRetard - aRetard;
      const priOrder: Record<PrioriteTache, number> = { urgente: 0, haute: 1, normale: 2 };
      if (priOrder[a.priorite] !== priOrder[b.priorite]) return priOrder[a.priorite] - priOrder[b.priorite];
      return b.date_creation.localeCompare(a.date_creation);
    });
  }, [taches]);

  const handleCompleter = async (id: number) => {
    try { await completer(id).unwrap(); }
    catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };
  const handleRouvrir = async (id: number) => {
    try { await rouvrir(id).unwrap(); }
    catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try { await deleteTache(id).unwrap(); }
    catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const cardCls = "rounded-xl border " + (isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200");

  return (
    <div className="space-y-4">
      {/* Barre filtres */}
      <div className={`${cardCls} p-3 flex flex-wrap gap-2 items-center`}>
        <Filter size={14} className="text-gray-400" />
        <span className="text-xs font-medium text-gray-500">Statut :</span>
        {(['', 'a_faire', 'en_cours', 'terminee', 'annulee'] as const).map(s => (
          <button
            key={s || 'all'}
            onClick={() => setFiltreStatut(s as any)}
            className={`px-2.5 py-1 text-xs rounded-md font-medium border transition ${
              filtreStatut === s
                ? 'bg-blue-600 text-white border-blue-600'
                : isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >{s ? STATUT_LABELS[s as StatutTache] : 'Toutes'}</button>
        ))}
        {isResponsable && poles.length > 0 && (
          <>
            <span className="mx-1 text-gray-300 dark:text-gray-700">|</span>
            <span className="text-xs font-medium text-gray-500">Pôle :</span>
            <select
              value={filtrePole}
              onChange={e => setFiltrePole(e.target.value)}
              className={`px-2 py-1 text-xs rounded-md border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
            >
              <option value="">Tous</option>
              {poles.map(p => <option key={p.id} value={p.nom}>{p.nom}</option>)}
            </select>
          </>
        )}
        <div className="flex-1" />
        <button onClick={() => refetch()}
          className={`p-1.5 rounded ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
          title="Rafraîchir"
        ><RefreshCw size={13} /></button>
        {isResponsable && (
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md"
          >
            <Plus size={14} /> Nouvelle tâche
          </button>
        )}
      </div>

      <div className={`${cardCls} overflow-hidden`}>
        {tachesFiltrees.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Aucune tâche pour ces filtres.</div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {tachesFiltrees.map(t => (
              <TacheRow
                key={t.id}
                tache={t}
                isDark={isDark}
                userId={userId}
                isResponsable={isResponsable}
                onCompleter={handleCompleter}
                onRouvrir={handleRouvrir}
                onDelete={handleDelete}
                onOpen={() => setTacheDetail(t)}
              />
            ))}
          </ul>
        )}
      </div>

      {showCreate && (
        <CreateTacheModal isDark={isDark} onClose={() => setShowCreate(false)} onCreated={() => setShowCreate(false)} />
      )}

      {tacheDetail && (
        <TacheDetailModal
          tache={tacheDetail}
          isDark={isDark}
          isResponsable={isResponsable}
          userId={userId}
          onClose={() => setTacheDetail(null)}
        />
      )}
    </div>
  );
};

const TacheRow: React.FC<{
  tache: TacheScolarite;
  isDark: boolean;
  userId: number;
  isResponsable: boolean;
  onCompleter: (id: number) => void;
  onRouvrir: (id: number) => void;
  onDelete: (id: number) => void;
  onOpen: () => void;
}> = ({ tache, isDark, userId, isResponsable, onCompleter, onRouvrir, onDelete, onOpen }) => {
  const assignees = tache.assignees && tache.assignees.length > 0
    ? tache.assignees
    : (tache.assignee_a && tache.assignee_a_nom
        ? [{ id: tache.assignee_a, nom: tache.assignee_a_nom }]
        : []);
  const isMine = assignees.some(a => a.id === userId) || tache.type_tache === 'equipe';
  const done = tache.statut === 'terminee';

  return (
    <li
      className={`px-3 py-3 flex items-start gap-3 hover:${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} transition ${
        tache.est_en_retard ? (isDark ? 'border-l-4 border-red-500 bg-red-900/5' : 'border-l-4 border-red-500 bg-red-50/50') : ''
      }`}
    >
      <button
        onClick={() => (done ? (isResponsable && onRouvrir(tache.id)) : (isMine && onCompleter(tache.id)))}
        disabled={done ? !isResponsable : !isMine}
        className="mt-0.5 flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
        title={done ? (isResponsable ? 'Ré-ouvrir' : 'Déjà terminée') : (isMine ? 'Marquer terminée' : 'Non assignée à vous')}
      >
        {done
          ? <CheckCircle2 size={20} className="text-emerald-500" />
          : <Circle size={20} className="text-gray-400 hover:text-blue-500 transition-colors" />}
      </button>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium text-sm ${done ? 'line-through text-gray-400' : ''}`}>{tache.titre}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${PRIORITE_STYLE[tache.priorite]}`}>
            {tache.priorite === 'urgente' ? 'Urgent' : tache.priorite === 'haute' ? 'Haute' : 'Normale'}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${STATUT_STYLE[tache.statut]}`}>
            {STATUT_LABELS[tache.statut]}
          </span>
          {tache.est_en_retard && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-semibold">EN RETARD</span>
          )}
          {tache.type_tache === 'equipe' && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 inline-flex items-center gap-0.5">
              <Users size={10} /> Équipe
            </span>
          )}
          {tache.poles && tache.poles.length > 0 && tache.poles.map(p => (
            <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 inline-flex items-center gap-0.5">
              <Layers size={9} /> {p}
            </span>
          ))}
        </div>
        {tache.description && (
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'} line-clamp-1`}>{tache.description}</p>
        )}
        <div className="text-[11px] mt-1.5 flex items-center gap-3 text-gray-500 flex-wrap">
          {assignees.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <User size={11} />
              {assignees.slice(0, 2).map(a => a.nom).join(', ')}
              {assignees.length > 2 && ` +${assignees.length - 2}`}
            </span>
          )}
          {tache.date_echeance && (
            <span className={`inline-flex items-center gap-1 ${tache.est_en_retard ? 'text-red-500 font-semibold' : ''}`}>
              <Clock size={11} /> Échéance : {formatDate(tache.date_echeance)}
            </span>
          )}
          {tache.nb_commentaires > 0 && (
            <span className="inline-flex items-center gap-1">
              <MessageSquare size={11} /> {tache.nb_commentaires}
            </span>
          )}
          {done && tache.complete_par_nom && (
            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={11} /> {tache.complete_par_nom} · {formatDateTime(tache.date_completion)}
            </span>
          )}
          {tache.dernier_rappel_push && (
            <span className="inline-flex items-center gap-1 text-blue-500 dark:text-blue-400" title="Dernier rappel push envoyé">
              <BellRing size={11} /> {formatDateTime(tache.dernier_rappel_push)}
            </span>
          )}
        </div>
      </div>
      {isResponsable && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(tache.id); }}
          className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded flex-shrink-0"
          title="Supprimer"
        ><Trash2 size={13} /></button>
      )}
    </li>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Modal création tâche — multi-agents + multi-pôles
// ═══════════════════════════════════════════════════════════════════════
const CreateTacheModal: React.FC<{
  isDark: boolean; onClose: () => void; onCreated: () => void;
}> = ({ isDark, onClose, onCreated }) => {
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [typeTache, setTypeTache] = useState<TypeTache>('individuelle');
  const [priorite, setPriorite] = useState<PrioriteTache>('normale');
  const [assignees, setAssignees] = useState<Set<number>>(new Set());
  const [selPoles, setSelPoles] = useState<Set<string>>(new Set());
  const [dateEcheance, setDateEcheance] = useState('');
  const [searchAgent, setSearchAgent] = useState('');
  const [filtreAgentRole, setFiltreAgentRole] = useState<string>('');
  const [filtreAgentPole, setFiltreAgentPole] = useState<string>('');

  const { data: agents = [] } = useListAgentsQuery();
  const { data: poles = [] } = useListPolesQuery();
  const [createTache, { isLoading: creating }] = useCreateTacheMutation();

  const toggleAgent = (id: number) => {
    setAssignees(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const togglePole = (nom: string) => {
    setSelPoles(prev => {
      const next = new Set(prev);
      if (next.has(nom)) next.delete(nom); else next.add(nom);
      return next;
    });
  };

  // Agents filtrés
  const agentsFiltres = useMemo(() => {
    return agents.filter(a => {
      if (filtreAgentRole && a.role !== filtreAgentRole) return false;
      if (filtreAgentPole && !(a.poles || []).includes(filtreAgentPole)) return false;
      if (searchAgent.trim()) {
        const q = searchAgent.trim().toLowerCase();
        if (!a.nom_complet.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [agents, searchAgent, filtreAgentRole, filtreAgentPole]);

  // Action rapide : sélectionner tous les agents des pôles cochés
  const selectionnerParPoles = () => {
    if (selPoles.size === 0) return;
    const next = new Set(assignees);
    agents.forEach(a => {
      if ((a.poles || []).some(p => selPoles.has(p))) next.add(a.id);
    });
    setAssignees(next);
  };

  const submit = async () => {
    if (!titre.trim()) { alert('Titre requis'); return; }
    if (typeTache === 'individuelle' && assignees.size === 0) {
      alert('Sélectionnez au moins un agent'); return;
    }
    try {
      await createTache({
        titre: titre.trim(),
        description: description.trim(),
        type_tache: typeTache,
        priorite,
        assignees_ids: typeTache === 'individuelle' ? Array.from(assignees) : [],
        poles: Array.from(selPoles),
        date_echeance: dateEcheance || null,
      } as any).unwrap();
      onCreated();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
  }`;
  const labelCls = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 block mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3">
      <div className={`rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <header className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <h3 className="font-semibold">Nouvelle tâche</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Titre + description */}
          <div>
            <label className={labelCls}>Titre *</label>
            <input value={titre} onChange={e => setTitre(e.target.value)} autoFocus className={inputCls}
              placeholder="Ex : Vérifier les frais MAR2 avant vendredi" />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className={inputCls}
              placeholder="Détails, contexte, contact…" />
          </div>

          {/* Type + priorité */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Type</label>
              <div className="flex gap-1">
                {(['individuelle', 'equipe'] as const).map(t => (
                  <button key={t} onClick={() => setTypeTache(t)}
                    className={`flex-1 px-2 py-2 text-xs rounded-lg border font-medium ${
                      typeTache === t ? 'bg-blue-600 text-white border-blue-600' :
                      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'
                    }`}
                  >{t === 'individuelle' ? 'Individuelle' : 'Équipe'}</button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {typeTache === 'individuelle'
                  ? 'Assignée à un ou plusieurs agents précis.'
                  : 'N\'importe quel agent (des pôles ciblés) peut la cocher.'}
              </p>
            </div>
            <div>
              <label className={labelCls}>Priorité</label>
              <div className="flex gap-1">
                {(['normale', 'haute', 'urgente'] as const).map(p => (
                  <button key={p} onClick={() => setPriorite(p)}
                    className={`flex-1 px-2 py-2 text-xs rounded-lg border font-medium capitalize ${
                      priorite === p ? 'bg-blue-600 text-white border-blue-600' :
                      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'
                    }`}
                  >{p}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Pôles ciblés */}
          {poles.length > 0 && (
            <div>
              <label className={labelCls}>
                Pôles ciblés {selPoles.size > 0 && <span className="text-blue-500 normal-case ml-1">({selPoles.size})</span>}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {poles.map(p => {
                  const sel = selPoles.has(p.nom);
                  return (
                    <button
                      key={p.id}
                      onClick={() => togglePole(p.nom)}
                      className={`px-2.5 py-1 text-xs rounded-full border font-medium transition ${
                        sel
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >{p.nom}</button>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {typeTache === 'equipe'
                  ? 'La tâche apparaît uniquement chez les agents de ces pôles. Laissez vide pour tout le service.'
                  : 'Filtre visuel ; l\'assignation se fait dans la liste ci-dessous.'}
              </p>
            </div>
          )}

          {/* Assignation multi-agents */}
          {typeTache === 'individuelle' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls + ' mb-0'}>
                  Agents assignés {assignees.size > 0 && <span className="text-blue-500 normal-case ml-1">({assignees.size} sélectionné{assignees.size > 1 ? 's' : ''})</span>}
                </label>
                {selPoles.size > 0 && (
                  <button onClick={selectionnerParPoles}
                    className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline">
                    + Tous les agents des pôles cochés
                  </button>
                )}
              </div>
              <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`}>
                <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex gap-2`}>
                  <div className="relative flex-1">
                    <Search size={12} className="absolute left-2 top-2 text-gray-400" />
                    <input value={searchAgent} onChange={e => setSearchAgent(e.target.value)}
                      placeholder="Rechercher un agent…"
                      className={`w-full pl-7 pr-2 py-1.5 text-xs rounded border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                  </div>
                  <select value={filtreAgentRole} onChange={e => setFiltreAgentRole(e.target.value)}
                    className={`px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-900 border-gray-700 text-gray-200' : 'bg-white border-gray-300'}`}>
                    <option value="">Tous rôles</option>
                    <option value="admin">Admin</option>
                    <option value="agent">Agent</option>
                    <option value="super_admin">Super admin</option>
                  </select>
                  {poles.length > 0 && (
                    <select value={filtreAgentPole} onChange={e => setFiltreAgentPole(e.target.value)}
                      className={`px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-900 border-gray-700 text-gray-200' : 'bg-white border-gray-300'}`}>
                      <option value="">Tous pôles</option>
                      {poles.map(p => <option key={p.id} value={p.nom}>{p.nom}</option>)}
                    </select>
                  )}
                </div>
                <div className="max-h-52 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
                  {agentsFiltres.length === 0 && (
                    <p className="p-3 text-xs text-gray-500 text-center">Aucun agent pour ces filtres.</p>
                  )}
                  {agentsFiltres.map(a => {
                    const sel = assignees.has(a.id);
                    return (
                      <button key={a.id} onClick={() => toggleAgent(a.id)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 transition ${
                          sel ? 'bg-blue-50 dark:bg-blue-900/30' :
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                        }`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          sel ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {sel && <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" /></svg>}
                        </div>
                        <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-300 flex-shrink-0">
                          {initiales(a.nom_complet)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{a.nom_complet}</div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {a.role} {a.poles?.length ? ' · ' + a.poles.join(' · ') : ''}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Échéance */}
          <div>
            <label className={labelCls}>Date d'échéance (optionnelle)</label>
            <input type="date" value={dateEcheance} onChange={e => setDateEcheance(e.target.value)} className={inputCls} />
          </div>
        </div>

        <footer className={`px-4 py-3 border-t flex justify-end gap-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <button onClick={onClose}
            className={`px-3 py-1.5 text-sm rounded-md ${isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            Annuler
          </button>
          <button onClick={submit} disabled={creating}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
          >
            <Plus size={14} /> {creating ? 'Création…' : 'Créer la tâche'}
          </button>
        </footer>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Modal détail tâche + commentaires + rappel
// ═══════════════════════════════════════════════════════════════════════
const TacheDetailModal: React.FC<{
  tache: TacheScolarite; isDark: boolean; isResponsable: boolean; userId: number; onClose: () => void;
}> = ({ tache: tInit, isDark, isResponsable, userId, onClose }) => {
  const [commentInput, setCommentInput] = useState('');
  const [commenter, { isLoading: sending }] = useCommenterTacheMutation();
  const [rappeler, { isLoading: rappelPending }] = useRappelerTacheMutation();
  const { data: refreshed } = useListTachesQuery(undefined, { pollingInterval: 5000 });
  const tache = useMemo(() => refreshed?.find(t => t.id === tInit.id) || tInit, [refreshed, tInit]);
  const assignees = tache.assignees && tache.assignees.length > 0
    ? tache.assignees
    : (tache.assignee_a && tache.assignee_a_nom
        ? [{ id: tache.assignee_a, nom: tache.assignee_a_nom }]
        : []);
  const [feedback, setFeedback] = useState<string | null>(null);

  const submit = async () => {
    if (!commentInput.trim()) return;
    try {
      await commenter({ id: tache.id, contenu: commentInput.trim() }).unwrap();
      setCommentInput('');
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const handleRappeler = async () => {
    if (!window.confirm(`Envoyer un rappel push aux ${assignees.length} destinataire(s) ?`)) return;
    setFeedback(null);
    try {
      const r = await rappeler(tache.id).unwrap();
      setFeedback(`${r.nb_pushs_envoyees} push(s) envoyée(s) sur ${r.nb_destinataires} destinataires.`);
    } catch (e: any) {
      setFeedback(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3">
      <div className={`rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <header className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <h3 className="font-semibold text-sm truncate">{tache.titre}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className={`text-[11px] px-2 py-0.5 rounded border font-medium ${STATUT_STYLE[tache.statut]}`}>{STATUT_LABELS[tache.statut]}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded font-medium capitalize ${PRIORITE_STYLE[tache.priorite]}`}>{tache.priorite}</span>
            {tache.type_tache === 'equipe' && (
              <span className="text-[11px] px-2 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 inline-flex items-center gap-1">
                <Users size={10} /> Équipe
              </span>
            )}
            {tache.poles?.map(p => (
              <span key={p} className="text-[11px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 inline-flex items-center gap-1">
                <Layers size={10} /> {p}
              </span>
            ))}
          </div>

          {tache.description && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Description</div>
              <p className="text-sm whitespace-pre-wrap">{tache.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider mb-1">Assignée à ({assignees.length})</div>
              {assignees.length === 0 ? (
                <div className="text-gray-500">— Équipe (pôles ci-dessus) —</div>
              ) : (
                <ul className="space-y-1">
                  {assignees.map(a => (
                    <li key={a.id} className="inline-flex items-center gap-1.5 text-sm">
                      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[9px] font-bold">
                        {initiales(a.nom)}
                      </div>
                      <span>{a.nom}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <div>
                <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider mb-0.5">Créée par</div>
                <div className="text-sm">{tache.creee_par_nom || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider mb-0.5">Créée le</div>
                <div className="text-sm">{formatDateTime(tache.date_creation)}</div>
              </div>
              <div>
                <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider mb-0.5">Échéance</div>
                <div className={`text-sm ${tache.est_en_retard ? 'text-red-500 font-semibold' : ''}`}>
                  {formatDate(tache.date_echeance)}
                </div>
              </div>
              {tache.date_completion && (
                <>
                  <div>
                    <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider mb-0.5">Terminée par</div>
                    <div className="text-sm text-emerald-600 dark:text-emerald-400">{tache.complete_par_nom}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider mb-0.5">Terminée le</div>
                    <div className="text-sm">{formatDateTime(tache.date_completion)}</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Action rappel */}
          {isResponsable && tache.statut !== 'terminee' && assignees.length > 0 && (
            <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <BellRing size={14} className="text-blue-500" />
                <span className="text-xs font-semibold">Rappeler les {assignees.length} assigné(s) par push</span>
                <div className="flex-1" />
                {tache.dernier_rappel_push && (
                  <span className="text-[10px] text-gray-500">Dernier : {formatDateTime(tache.dernier_rappel_push)}</span>
                )}
                <button onClick={handleRappeler} disabled={rappelPending}
                  className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold disabled:opacity-50">
                  {rappelPending ? 'Envoi…' : 'Envoyer'}
                </button>
              </div>
              {feedback && <div className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">{feedback}</div>}
            </div>
          )}

          {/* Commentaires */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
              <MessageSquare size={12} /> Fil de discussion ({tache.commentaires.length})
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {tache.commentaires.length === 0 && (
                <p className="text-xs text-gray-400 italic">Aucun commentaire pour l'instant.</p>
              )}
              {tache.commentaires.map(c => {
                const mine = c.auteur === userId;
                return (
                  <div key={c.id} className={`p-2 rounded-lg ${
                    mine
                      ? 'bg-blue-50 dark:bg-blue-900/20 ml-8'
                      : isDark ? 'bg-gray-800 mr-8' : 'bg-gray-50 mr-8'
                  }`}>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-1">
                      <strong className="text-gray-700 dark:text-gray-300">{c.auteur_nom}</strong>
                      {c.auteur_role && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 capitalize">
                          {c.auteur_role.replace('_', ' ')}
                        </span>
                      )}
                      <span>· {formatDateTime(c.date_creation)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{c.contenu}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className={`px-3 py-3 border-t flex gap-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <input
            value={commentInput}
            onChange={e => setCommentInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="Écrire un commentaire…"
            className={`flex-1 px-3 py-2 text-sm rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`}
          />
          <button onClick={submit} disabled={sending || !commentInput.trim()}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          ><Send size={14} /></button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Onglet mon rapport du jour (+ historique perso)
// ═══════════════════════════════════════════════════════════════════════
const MonRapportTab: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const user = useSelector((s: RootState) => s.auth.user) as any;
  const navigate = useNavigate();
  const { data: rapport, refetch } = useGetRapportAujourdhuiQuery();
  const [saveRapport, { isLoading: saving }] = useSaveRapportMutation();
  const [resume, setResume] = useState('');
  const [difficultes, setDifficultes] = useState('');
  const [besoins, setBesoins] = useState('');
  const [visibilite, setVisibilite] = useState<'public' | 'prive'>('public');
  const [tabHist, setTabHist] = useState<'jour' | 'historique'>('jour');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string; sub?: string } | null>(null);

  // Date du rapport en cours d'édition (aujourd'hui par défaut).
  // Permet à un agent de rédiger le rapport d'un jour passé pour rattraper
  // un oubli (utile pour lever un verrou du backend).
  const [dateRapport, setDateRapport] = useState<string>(() => isoAujourdhui());
  const estAujourdhui = dateRapport === isoAujourdhui();

  // Rapport chargé : soit celui d'aujourd'hui (via l'endpoint dédié),
  // soit celui d'un jour passé (via listRapports filtré sur la date).
  const { data: rapportsJour = [] } = useListRapportsQuery(
    { depuis: dateRapport, jusqua: dateRapport },
    { skip: estAujourdhui },
  );
  const rapportEnCours = estAujourdhui ? rapport : (rapportsJour[0] || null);

  // Historique perso (7 derniers jours par défaut)
  const [depuis, setDepuis] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return d.toISOString().slice(0, 10);
  });
  const { data: mesRapports = [] } = useListRapportsQuery({ depuis }, {
    skip: tabHist !== 'historique',
  });

  useEffect(() => {
    // Charge le contenu du rapport correspondant à la date sélectionnée
    if (rapportEnCours) {
      setResume(rapportEnCours.resume_libre || '');
      setDifficultes(rapportEnCours.difficultes || '');
      setBesoins(rapportEnCours.besoins || '');
      setVisibilite(((rapportEnCours as any).visibilite === 'prive') ? 'prive' : 'public');
    } else {
      // Aucun rapport pour ce jour → formulaire vierge
      setResume('');
      setDifficultes('');
      setBesoins('');
      setVisibilite('public');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rapportEnCours?.id, dateRapport]);

  const submit = async () => {
    setFeedback(null);
    // Validation contenu côté client : évite un aller-retour serveur pour
    // un rapport manifestement vide (message identique côté backend).
    const contenu = (resume + difficultes + besoins).trim();
    const nbTaches = rapportEnCours?.taches_terminees_ids?.length || 0;
    if (contenu.length < 20 && nbTaches === 0) {
      setFeedback({
        type: 'error',
        text: 'Rapport trop court ou vide.',
        sub: 'Décrivez ce que vous avez fait, les difficultés ou vos besoins (min. 20 caractères au total) — ou cochez au moins une tâche du jour.',
      });
      return;
    }
    try {
      const result: any = await saveRapport({
        date_rapport: dateRapport,
        resume_libre: resume, difficultes, besoins,
        visibilite,
      } as any).unwrap();
      refetch();
      const dateFormatee = new Date(dateRapport).toLocaleDateString('fr-FR');
      // Le backend renvoie acces_debloque=true si le verrou a été levé après
      // ce rapport → l'agent peut retourner au dashboard.
      const debloque = result?.acces_debloque === true;
      const motifResiduel = result?.motif_verrou_residuel || '';
      setFeedback({
        type: 'success',
        text: estAujourdhui
          ? '✓ Rapport enregistré avec succès.'
          : `✓ Rapport du ${dateFormatee} enregistré.`,
        sub: debloque
          ? "Accès aux autres pages débloqué. Vous pouvez retourner au tableau de bord."
          : (motifResiduel ? `⚠ Reste bloquant : ${motifResiduel}` : ''),
      });
      // Redirige vers le dashboard après 1,8s si l'accès est débloqué.
      // Navigation react-router (pas de reload) → l'app garde son état
      // et la garde VerrouRapportGuard a déjà été rafraîchie via
      // l'invalidation du tag 'Verrou' par saveRapport.
      if (debloque && estAujourdhui) {
        setTimeout(() => {
          try { navigate('/dashboard'); } catch { /* ignore */ }
        }, 1800);
      } else {
        setTimeout(() => setFeedback(null), 6000);
      }
    } catch (e: any) {
      setFeedback({
        type: 'error',
        text: e?.data?.detail || 'Erreur lors de l\'enregistrement.',
        sub: e?.data?.code === 'RAPPORT_VIDE' ? 'Merci de compléter votre rapport avant d\'enregistrer.' : '',
      });
    }
  };

  const chargerRapport = (dateIso: string) => {
    setDateRapport(dateIso);
    setTabHist('jour');
  };

  const cardCls = "rounded-xl border " + (isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200");
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`;
  const labelCls = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 block mb-1';

  return (
    <div className="space-y-4">
      {/* Sous-onglets : Aujourd'hui / Historique */}
      <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button onClick={() => setTabHist('jour')}
          className={`px-3 py-1.5 text-xs font-medium ${
            tabHist === 'jour' ? 'bg-blue-600 text-white' :
            isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
          }`}>Aujourd'hui</button>
        <button onClick={() => setTabHist('historique')}
          className={`px-3 py-1.5 text-xs font-medium border-l ${
            tabHist === 'historique' ? 'bg-blue-600 text-white border-blue-600' :
            isDark ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-white text-gray-700 border-gray-200'
          }`}>Historique</button>
      </div>

      {tabHist === 'jour' && (
        <div className={`${cardCls} p-4`}>
          {/* Sélecteur de date : par défaut aujourd'hui, mais l'agent peut
              choisir un jour passé pour rattraper un rapport oublié. */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <FileText size={16} className="text-blue-500" />
            <h3 className="font-semibold text-sm">Mon rapport</h3>
            <div className="flex items-center gap-1.5 ml-2">
              <Calendar size={12} className="text-gray-400" />
              <input type="date" value={dateRapport}
                max={isoAujourdhui()}
                onChange={(e) => setDateRapport(e.target.value || isoAujourdhui())}
                className={`px-2 py-1 rounded border text-xs tabular-nums ${
                  isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
                }`} />
              {!estAujourdhui && (
                <button onClick={() => setDateRapport(isoAujourdhui())}
                  className="text-[10px] text-blue-500 hover:underline">
                  → Aujourd'hui
                </button>
              )}
            </div>
            {rapportEnCours?.lu_par_responsable && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium">
                Lu par le responsable
              </span>
            )}
            {!estAujourdhui && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-medium">
                Rapport rétroactif
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500 mb-3">
            {formatDate(dateRapport)}
            {!estAujourdhui && ' · vous rédigez pour un jour passé pour rattraper un rapport manquant'}
          </div>
          {rapportEnCours && rapportEnCours.taches_terminees_titres.length > 0 && (
            <div className={`mb-4 rounded-lg p-3 ${isDark ? 'bg-gray-800' : 'bg-emerald-50'}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1">
                Tâches terminées ce jour ({rapportEnCours.taches_terminees_titres.length})
              </div>
              <ul className="text-sm space-y-0.5">
                {rapportEnCours.taches_terminees_titres.map((t, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Ce que j'ai fait / observé</label>
              <textarea value={resume} onChange={e => setResume(e.target.value)} rows={4} className={inputCls}
                placeholder="Ce qui n'est pas déjà dans les tâches cochées…" />
            </div>
            <div>
              <label className={labelCls}>Difficultés rencontrées</label>
              <textarea value={difficultes} onChange={e => setDifficultes(e.target.value)} rows={3} className={inputCls}
                placeholder="Blocages, problèmes techniques, besoin d'aide…" />
            </div>
            <div>
              <label className={labelCls}>Besoins du service</label>
              <textarea value={besoins} onChange={e => setBesoins(e.target.value)} rows={3} className={inputCls}
                placeholder="Matériel, fournitures, ressources nécessaires…" />
            </div>

            {/* Sélecteur de visibilité — public par défaut, privé pour un
                rapport que seuls l'auteur et les admins doivent voir. */}
            <div>
              <label className={labelCls}>Visibilité du rapport</label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setVisibilite('public')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                    visibilite === 'public'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : (isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50')
                  }`}
                >
                  🌐 Public
                  <span className={visibilite === 'public' ? 'text-white/85' : 'text-gray-400'}>· visible par toute l'équipe</span>
                </button>
                <button
                  type="button"
                  onClick={() => setVisibilite('prive')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                    visibilite === 'prive'
                      ? 'bg-amber-600 text-white border-amber-600'
                      : (isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50')
                  }`}
                >
                  🔒 Privé
                  <span className={visibilite === 'prive' ? 'text-white/85' : 'text-gray-400'}>· visible par moi + admin</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={submit} disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 text-sm font-semibold"
              >
                <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer le rapport'}
              </button>
            </div>
            {feedback && (
              <div className={`mt-2 p-3 rounded-lg text-sm border ${
                feedback.type === 'success'
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
              }`}>
                <div className="font-semibold">{feedback.text}</div>
                {feedback.sub && <div className="text-xs mt-0.5 opacity-90">{feedback.sub}</div>}
              </div>
            )}
          </div>
          {/* Fil de discussion : commentaires admin + actions à mener */}
          {rapportEnCours?.id && user?.id && (
            <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <FilCommentairesRapport
                rapportId={rapportEnCours.id}
                auteurRapportId={rapportEnCours.auteur}
                utilisateurCourantId={user.id}
                roleCourant={user.role || ''}
              />
            </div>
          )}
        </div>
      )}

      {tabHist === 'historique' && (
        <div className="space-y-3">
          <div className={`${cardCls} p-3 flex items-center gap-2 text-xs flex-wrap`}>
            <Calendar size={13} className="text-gray-400" />
            <span className="text-gray-500">Depuis le</span>
            <input type="date" value={depuis} onChange={e => setDepuis(e.target.value)}
              className={`px-2 py-1 rounded border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
            <span className="text-gray-500">{mesRapports.length} rapport(s)</span>
            <div className="flex-1" />
            <button onClick={() => chargerRapport(isoAujourdhui())}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700">
              <FileText size={11} /> Rédiger un rapport d'un autre jour
            </button>
          </div>
          {mesRapports.length === 0 ? (
            <div className={`${cardCls} p-8 text-center text-sm text-gray-500`}>
              Aucun rapport sur cette période.
            </div>
          ) : (
            mesRapports.map(r => (
              <div key={r.id} className="relative">
                <RapportCard r={r} isDark={isDark} showAuteur={false} showBoutonLu={false} onMarquerLu={() => {}} />
                <button onClick={() => chargerRapport(r.date_rapport)}
                  className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded border border-blue-500 text-blue-500 dark:text-blue-400 bg-white/70 dark:bg-gray-900/70 hover:bg-blue-500 hover:text-white font-semibold">
                  Modifier
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Onglet rapports agents (responsable) — filtres jour/semaine/pôle/agent
// ═══════════════════════════════════════════════════════════════════════
const RapportsAgentsTab: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const [periode, setPeriode] = useState<'jour' | 'semaine' | 'personnalise'>('semaine');
  const [depuis, setDepuis] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return d.toISOString().slice(0, 10);
  });
  const [pole, setPole] = useState('');
  const [agent, setAgent] = useState<number | ''>('');

  const { data: poles = [] } = useListPolesQuery();
  const { data: agents = [] } = useListAgentsQuery();
  const params: any = {};
  if (periode === 'jour' || periode === 'semaine') params.periode = periode;
  if (periode === 'personnalise') params.depuis = depuis;
  if (pole) params.pole = pole;
  if (agent) params.auteur = Number(agent);
  const { data: rapports = [] } = useListRapportsQuery(params);
  const [marquerLu] = useMarquerRapportLuMutation();

  // Regroupement par jour
  const grouped = useMemo(() => {
    const map: Record<string, typeof rapports> = {};
    rapports.forEach(r => {
      const k = r.date_rapport;
      if (!map[k]) map[k] = [];
      map[k].push(r);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [rapports]);

  const cardCls = "rounded-xl border " + (isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200");

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className={`${cardCls} p-3 flex flex-wrap gap-2 items-center`}>
        <Filter size={14} className="text-gray-400" />
        <span className="text-xs font-medium text-gray-500">Période :</span>
        {(['jour', 'semaine', 'personnalise'] as const).map(p => (
          <button key={p} onClick={() => setPeriode(p)}
            className={`px-2.5 py-1 text-xs rounded-md font-medium border ${
              periode === p ? 'bg-blue-600 text-white border-blue-600' :
              isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
            }`}>
            {p === 'jour' ? "Aujourd'hui" : p === 'semaine' ? '7 derniers jours' : 'Personnalisé'}
          </button>
        ))}
        {periode === 'personnalise' && (
          <input type="date" value={depuis} onChange={e => setDepuis(e.target.value)}
            className={`px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
        )}
        {poles.length > 0 && (
          <>
            <span className="mx-1 text-gray-300 dark:text-gray-700">|</span>
            <span className="text-xs font-medium text-gray-500">Pôle :</span>
            <select value={pole} onChange={e => setPole(e.target.value)}
              className={`px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>
              <option value="">Tous</option>
              {poles.map(p => <option key={p.id} value={p.nom}>{p.nom}</option>)}
            </select>
          </>
        )}
        <span className="text-xs font-medium text-gray-500">Agent :</span>
        <select value={agent} onChange={e => setAgent(e.target.value ? Number(e.target.value) : '')}
          className={`px-2 py-1 text-xs rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>
          <option value="">Tous</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.nom_complet}</option>)}
        </select>
        <div className="flex-1" />
        <span className="text-xs text-gray-500 tabular-nums">
          {rapports.length} rapport(s)
        </span>
      </div>

      {grouped.length === 0 ? (
        <div className={`${cardCls} p-10 text-center text-sm text-gray-500`}>
          Aucun rapport pour ces filtres.
        </div>
      ) : (
        grouped.map(([jour, list]) => (
          <section key={jour} className="space-y-2">
            <h3 className="text-[11px] uppercase tracking-widest text-gray-500 font-bold px-2 flex items-center gap-2">
              <Calendar size={12} /> {formatDate(jour)}
              <span className="text-gray-400 normal-case font-normal">· {list.length} rapport{list.length > 1 ? 's' : ''}</span>
            </h3>
            <div className="space-y-2">
              {list.map(r => (
                <RapportCard key={r.id} r={r} isDark={isDark}
                  showAuteur showBoutonLu
                  onMarquerLu={() => marquerLu(r.id)} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

// ─── Carte rapport réutilisable ─────────────────────────────────────────
const RapportCard: React.FC<{
  r: any;
  isDark: boolean;
  showAuteur: boolean;
  showBoutonLu: boolean;
  onMarquerLu: () => void;
  defaultOpen?: boolean;
}> = ({ r, isDark, showAuteur, showBoutonLu, onMarquerLu, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const user = useSelector((s: RootState) => s.auth.user) as any;
  const cardCls = "rounded-xl border " + (isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200");
  const nbCommentaires = r.nb_commentaires || 0;
  const nbActionsOuvertes = r.nb_actions_ouvertes || 0;
  const nbNonLus = r.nb_non_lus_pour_moi || 0;
  return (
    <div className={cardCls}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-t-xl text-left">
        {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
        {showAuteur && (
          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-300 flex-shrink-0">
            {initiales(r.auteur_nom)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {showAuteur && <div className="font-semibold text-sm truncate">{r.auteur_nom}</div>}
          <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
            {!showAuteur && <span>{formatDate(r.date_rapport)}</span>}
            <span>{r.taches_terminees_titres.length} tâche(s) terminée(s)</span>
            {r.auteur_poles?.length > 0 && (
              <>· <span className="inline-flex items-center gap-0.5"><Layers size={10} /> {r.auteur_poles.join(', ')}</span></>
            )}
          </div>
        </div>
        {/* Badges : non-lus / actions ouvertes / commentaires */}
        {nbNonLus > 0 && (
          <span title={`${nbNonLus} message(s) non lu(s)`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-500 text-white font-bold">
            <MessageSquare size={10} /> {nbNonLus}
          </span>
        )}
        {nbActionsOuvertes > 0 && (
          <span title={`${nbActionsOuvertes} action(s) en cours`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold">
            <ClipboardList size={10} /> {nbActionsOuvertes}
          </span>
        )}
        {nbCommentaires > 0 && nbNonLus === 0 && (
          <span title={`${nbCommentaires} message(s) dans le fil`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
            <MessageSquare size={10} /> {nbCommentaires}
          </span>
        )}
        {r.lu_par_responsable ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-medium">Lu</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-semibold">Non lu</span>
        )}
      </button>
      {open && (
        <div className={`px-4 pb-4 space-y-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'} text-sm`}>
          {r.taches_terminees_titres.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mt-3 mb-1">Tâches terminées</div>
              <ul className="space-y-0.5">
                {r.taches_terminees_titres.map((t: string, i: number) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {r.resume_libre && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mt-2 mb-1">Résumé</div>
              <p className="whitespace-pre-wrap">{r.resume_libre}</p>
            </div>
          )}
          {r.difficultes && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mt-2 mb-1">Difficultés</div>
              <p className="whitespace-pre-wrap text-amber-700 dark:text-amber-300">{r.difficultes}</p>
            </div>
          )}
          {r.besoins && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mt-2 mb-1">Besoins</div>
              <p className="whitespace-pre-wrap text-blue-700 dark:text-blue-300">{r.besoins}</p>
            </div>
          )}
          {showBoutonLu && !r.lu_par_responsable && (
            <button onClick={onMarquerLu}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-md font-semibold"
            >
              <Eye size={12} /> Marquer comme lu
            </button>
          )}
          {/* Fil de discussion + actions à mener */}
          {user?.id && (
            <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <FilCommentairesRapport
                rapportId={r.id}
                auteurRapportId={r.auteur}
                utilisateurCourantId={user.id}
                roleCourant={user.role || ''}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Onglet Statistiques (responsable) — agrégation jour/pôle/agent
// ═══════════════════════════════════════════════════════════════════════
const StatsRapportsTab: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const [depuis, setDepuis] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return d.toISOString().slice(0, 10);
  });
  const { data: stats } = useStatsRapportsQuery({ depuis });
  const cardCls = "rounded-xl border " + (isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200");

  if (!stats) return <div className={`${cardCls} p-8 text-center text-sm text-gray-500`}>Chargement…</div>;

  const maxJour = Math.max(1, ...stats.par_jour.map(j => j.nb_rapports));
  const maxPole = Math.max(1, ...stats.par_pole.map(p => p.nb_rapports));

  return (
    <div className="space-y-4">
      <div className={`${cardCls} p-3 flex items-center gap-2 text-xs`}>
        <Calendar size={13} className="text-gray-400" />
        <span className="text-gray-500">Depuis le</span>
        <input type="date" value={depuis} onChange={e => setDepuis(e.target.value)}
          className={`px-2 py-1 rounded border text-xs ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
        <span className="text-gray-500 ml-auto tabular-nums">{stats.total_rapports} rapport(s) au total</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Par jour */}
        <div className={`${cardCls} p-4`}>
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <Calendar size={14} className="text-blue-500" /> Rapports par jour
          </h3>
          {stats.par_jour.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune donnée.</p>
          ) : (
            <ul className="space-y-1.5">
              {stats.par_jour.map(j => (
                <li key={j.jour} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-gray-500 tabular-nums">{formatDateShort(j.jour)}</span>
                  <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden relative">
                    <div className="h-full bg-blue-500" style={{ width: `${(j.nb_rapports / maxJour) * 100}%` }} />
                  </div>
                  <span className="w-16 text-right tabular-nums font-semibold">
                    {j.nb_rapports} <span className="text-gray-400 font-normal">({j.nb_lus} lus)</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Par pôle */}
        <div className={`${cardCls} p-4`}>
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <Layers size={14} className="text-blue-500" /> Rapports par pôle
          </h3>
          {stats.par_pole.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune donnée.</p>
          ) : (
            <ul className="space-y-1.5">
              {stats.par_pole.map(p => (
                <li key={p.pole} className="flex items-center gap-2 text-xs">
                  <span className="w-28 text-gray-700 dark:text-gray-300 truncate">{p.pole}</span>
                  <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${(p.nb_rapports / maxPole) * 100}%` }} />
                  </div>
                  <span className="w-10 text-right tabular-nums font-semibold">{p.nb_rapports}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Par agent */}
      <div className={`${cardCls} p-4`}>
        <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
          <User size={14} className="text-blue-500" /> Rapports par agent
        </h3>
        {stats.par_agent.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune donnée.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-1.5 pr-2">Agent</th>
                  <th className="text-left py-1.5 pr-2">Pôles</th>
                  <th className="text-right py-1.5 pr-2">Rapports</th>
                  <th className="text-right py-1.5">Lus</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800/50">
                {stats.par_agent.map(a => (
                  <tr key={a.user_id}>
                    <td className="py-1.5 pr-2 font-medium">{a.nom_complet}</td>
                    <td className="py-1.5 pr-2 text-gray-500 text-xs">{a.poles?.join(', ') || '—'}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{a.nb_rapports}</td>
                    <td className="py-1.5 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{a.nb_lus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Onglet Réunions — comptes rendus visibles à tous, éditables par auteur / admin
// ═══════════════════════════════════════════════════════════════════════
const ReunionsTab: React.FC<{
  isDark: boolean; isResponsable: boolean; userId: number;
}> = ({ isDark, isResponsable, userId }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<RapportReunion | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [depuis, setDepuis] = useState<string>('');
  const { data: reunions = [], refetch } = useListReunionsQuery({
    ...(search ? { q: search } : {}),
    ...(depuis ? { depuis } : {}),
  });
  const [supprimer] = useSupprimerReunionMutation();

  const toggle = (id: number) => setExpanded(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const canEdit = (r: RapportReunion): boolean =>
    isResponsable || (r.cree_par === userId);

  const handleDelete = async (r: RapportReunion) => {
    if (!window.confirm(`Supprimer le compte rendu « ${r.titre} » ? Cette action est définitive.`)) return;
    try { await supprimer(r.id).unwrap(); }
    catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const cardCls = "rounded-xl border " + (isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200");

  return (
    <div className="space-y-4">
      {/* Barre filtres + nouveau */}
      <div className={`${cardCls} p-3 flex flex-wrap gap-2 items-center`}>
        <Filter size={14} className="text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un titre, une décision…"
          className={`flex-1 min-w-[180px] px-2 py-1.5 text-sm rounded border ${
            isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
          }`} />
        <span className="text-xs text-gray-500">Depuis :</span>
        <input type="date" value={depuis} onChange={e => setDepuis(e.target.value)}
          className={`px-2 py-1 text-xs rounded border ${
            isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
          }`} />
        <button onClick={() => refetch()}
          className={`p-1.5 rounded ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}
          title="Rafraîchir"
        ><RefreshCw size={13} /></button>
        <button onClick={() => { setEditing(null); setShowCreate(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md"
        ><Plus size={14} /> Nouvelle réunion</button>
      </div>

      {reunions.length === 0 ? (
        <div className={`${cardCls} p-10 text-center text-sm text-gray-500`}>
          Aucun compte rendu de réunion pour ces filtres.
        </div>
      ) : (
        <div className="space-y-3">
          {reunions.map(r => {
            const open = expanded.has(r.id);
            return (
              <div key={r.id} className={cardCls}>
                <button onClick={() => toggle(r.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-t-xl text-left">
                  {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                  <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-300 flex-shrink-0">
                    <Users size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{r.titre}</div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-0.5">
                        <Calendar size={11} /> {formatDate(r.date_reunion)}
                      </span>
                      {(r.heure_debut || r.heure_fin) && (
                        <span>· {r.heure_debut?.slice(0, 5) || '?'}
                          {r.heure_fin ? ` – ${r.heure_fin.slice(0, 5)}` : ''}
                        </span>
                      )}
                      {r.lieu && <span>· {r.lieu}</span>}
                      {r.cree_par_nom && <span>· par {r.cree_par_nom}</span>}
                      {r.participants_noms.length > 0 && (
                        <span>· {r.participants_noms.length} participant(s)</span>
                      )}
                    </div>
                  </div>
                </button>
                {open && (
                  <div className={`px-4 pb-4 space-y-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'} text-sm`}>
                    {r.ordre_du_jour && (
                      <SectionText label="Ordre du jour" value={r.ordre_du_jour} isDark={isDark} />
                    )}
                    {r.resume && (
                      <SectionText label="Résumé" value={r.resume} isDark={isDark} />
                    )}
                    {r.decisions && (
                      <SectionText label="Décisions" value={r.decisions} isDark={isDark} accent="emerald" />
                    )}
                    {r.actions && (
                      <SectionText label="Actions à mener" value={r.actions} isDark={isDark} accent="amber" />
                    )}
                    {r.participants_noms.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mt-2 mb-1">Participants</div>
                        <div className="flex flex-wrap gap-1">
                          {r.participants_noms.map((p, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {canEdit(r) && (
                      <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                        <button onClick={() => { setEditing(r); setShowCreate(true); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">
                          Modifier
                        </button>
                        <button onClick={() => handleDelete(r)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded font-medium">
                          <Trash2 size={12} /> Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <ReunionModal isDark={isDark} editing={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); refetch(); }} />
      )}
    </div>
  );
};

const SectionText: React.FC<{
  label: string; value: string; isDark: boolean;
  accent?: 'emerald' | 'amber' | 'blue';
}> = ({ label, value, isDark, accent }) => {
  const textColor = accent === 'emerald' ? 'text-emerald-700 dark:text-emerald-300' :
                    accent === 'amber' ? 'text-amber-700 dark:text-amber-300' :
                    accent === 'blue' ? 'text-blue-700 dark:text-blue-300' :
                    isDark ? 'text-gray-200' : 'text-gray-800';
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mt-2 mb-1">{label}</div>
      <p className={`whitespace-pre-wrap ${textColor}`}>{value}</p>
    </div>
  );
};

const ReunionModal: React.FC<{
  isDark: boolean;
  editing: RapportReunion | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ isDark, editing, onClose, onSaved }) => {
  const [titre, setTitre] = useState(editing?.titre || '');
  const [dateReunion, setDateReunion] = useState(editing?.date_reunion || isoAujourdhui());
  const [heureDebut, setHeureDebut] = useState(editing?.heure_debut?.slice(0, 5) || '');
  const [heureFin, setHeureFin] = useState(editing?.heure_fin?.slice(0, 5) || '');
  const [lieu, setLieu] = useState(editing?.lieu || '');
  const [ordre, setOrdre] = useState(editing?.ordre_du_jour || '');
  const [resume, setResume] = useState(editing?.resume || '');
  const [decisions, setDecisions] = useState(editing?.decisions || '');
  const [actions, setActions] = useState(editing?.actions || '');
  const [participantsRaw, setParticipantsRaw] = useState(editing?.participants_noms?.join(', ') || '');
  const [creer, { isLoading: creating }] = useCreerReunionMutation();
  const [modifier, { isLoading: updating }] = useModifierReunionMutation();

  const submit = async () => {
    if (!titre.trim()) { alert('Titre requis'); return; }
    if (!dateReunion) { alert('Date requise'); return; }
    const participants = participantsRaw.split(',').map(s => s.trim()).filter(Boolean);
    const data: any = {
      titre: titre.trim(),
      date_reunion: dateReunion,
      heure_debut: heureDebut ? `${heureDebut}:00` : null,
      heure_fin: heureFin ? `${heureFin}:00` : null,
      lieu: lieu.trim(),
      ordre_du_jour: ordre.trim(),
      resume: resume.trim(),
      decisions: decisions.trim(),
      actions: actions.trim(),
      participants_noms: participants,
    };
    try {
      if (editing) await modifier({ id: editing.id, data }).unwrap();
      else await creer(data).unwrap();
      onSaved();
    } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
  }`;
  const labelCls = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 block mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3">
      <div className={`rounded-xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <header className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <h3 className="font-semibold">{editing ? 'Modifier la réunion' : 'Nouveau compte rendu de réunion'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <label className={labelCls}>Titre *</label>
            <input value={titre} onChange={e => setTitre(e.target.value)} autoFocus className={inputCls}
              placeholder="Ex : Point hebdomadaire scolarité — S32" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <label className={labelCls}>Date *</label>
              <input type="date" value={dateReunion} onChange={e => setDateReunion(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Heure début</label>
              <input type="time" value={heureDebut} onChange={e => setHeureDebut(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Heure fin</label>
              <input type="time" value={heureFin} onChange={e => setHeureFin(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Lieu</label>
              <input value={lieu} onChange={e => setLieu(e.target.value)} className={inputCls} placeholder="Salle Direction" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Participants (séparés par virgules)</label>
            <input value={participantsRaw} onChange={e => setParticipantsRaw(e.target.value)} className={inputCls}
              placeholder="M. DIALLO, Mme NDIAYE, M. KOUAKOU…" />
          </div>
          <div>
            <label className={labelCls}>Ordre du jour</label>
            <textarea value={ordre} onChange={e => setOrdre(e.target.value)} rows={3} className={inputCls}
              placeholder="1. Point sur les scans manquants&#10;2. Planification MAR2&#10;3. Divers" />
          </div>
          <div>
            <label className={labelCls}>Résumé des discussions</label>
            <textarea value={resume} onChange={e => setResume(e.target.value)} rows={4} className={inputCls}
              placeholder="Ce qui a été dit, les points soulevés…" />
          </div>
          <div>
            <label className={labelCls}>Décisions prises</label>
            <textarea value={decisions} onChange={e => setDecisions(e.target.value)} rows={3} className={inputCls}
              placeholder="Les décisions actées collectivement" />
          </div>
          <div>
            <label className={labelCls}>Actions à mener (avec responsable + échéance)</label>
            <textarea value={actions} onChange={e => setActions(e.target.value)} rows={3} className={inputCls}
              placeholder="• Vérifier les frais MAR2 (M. Kouakou, avant vendredi)&#10;• Contacter les 3 étudiants absents…" />
          </div>
        </div>
        <footer className={`px-4 py-3 border-t flex justify-end gap-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <button onClick={onClose}
            className={`px-3 py-1.5 text-sm rounded-md ${isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            Annuler
          </button>
          <button onClick={submit} disabled={creating || updating}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
          >
            {(creating || updating) ? 'Enregistrement…' : (editing ? 'Enregistrer' : 'Créer le compte rendu')}
          </button>
        </footer>
      </div>
    </div>
  );
};
